/* eslint-disable */
import React from "react";
import moment from "moment";
import _ from "lodash";

import { EuiFlexGroup, EuiFlexItem, EuiText, EuiTextColor } from "@elastic/eui";

import { FormattedMessage } from "react-intl";
import EntityLink from "../../../shared/EntityLink";

import TripSection from "../shared/TripSection";
import {
    extractResourceAttributes,
    mapResourceObjectToArray,
} from "../../../../utils/jsonApiUtils";
import TripBadge from "../shared/TripBadge";
import { LineThroughText } from "../../../shared/style";
import tripStatus from "../../constants/tripStates";
import * as S from "../../style";

const driverAvatarOptions = {
    name: "name",
    previewOptions: ["name", "id", "email", "phoneNumber", "type"],
};

const adminAvatarOptions = {
    name: "name",
    previewOptions: ["name", "id", "type"],
};

const generateActivityItem = (
    { actorName, actorType, id },
    date,
    actorOptions,
    actionDescription,
    subject,
    subjectOptions,
    prevSubject,
    subjectType
) => (
    <EuiFlexGroup
        gutterSize="none"
        alignItems="center"
        className="activity__line"
        key={new Date().getTime() + Math.random()}
    >
        <EuiFlexItem
            grow={false}
            component="span"
            className="tripActivityDate"
        >
            <EuiText
                size="xs"
                className="eui-displayInline activity__date"
            >
                <EuiTextColor
                    color="subdued"
                    component="span"
                >
                    {`${moment(new Date(date)).format("DD-MM-YYYY  h:mm:ss a")} `}
                </EuiTextColor>
            </EuiText>
        </EuiFlexItem>

        <S.TripActivityContent>
            <EntityLink
                entity={{ id, name: actorName, type: actorType }}
                options={actorOptions}
            />{" "}
            {actionDescription}{" "}
            {prevSubject &&
                (subjectOptions ? (
                    <EntityLink
                        entity={{ ...prevSubject, type: subjectType }}
                        options={subjectOptions}
                        lineThrough
                    />
                ) : (
                    <LineThroughText>
                        <b>{prevSubject} </b>
                    </LineThroughText>
                ))}
            {prevSubject && subject && <FormattedMessage id="to" />}
            {subject &&
                (subjectOptions ? (
                    <EntityLink
                        entity={{ ...subject, type: subjectType }}
                        options={subjectOptions}
                    />
                ) : (
                    <b> {subject}</b>
                ))}
        </S.TripActivityContent>
    </EuiFlexGroup>
);

const isStatusAction = (status) =>
    status === tripStatus.COMPLETED ||
    status === tripStatus.CANCELLED ||
    status === tripStatus.NOT_REQUESTED ||
    status === tripStatus.UNFULFILLED ||
    status === tripStatus.NOT_LOADED ||
    status === tripStatus.ABSENT;

export default function TripActivity({
    activities,
    pickupPoint,
    tripPoints,
    confirmedTripsWithAdmin,
    driverName,
}) {
    const generateActivity = (activity, pickupPoint, activities, activityIndex, changesPoint) => {
        const {
            changes,
            createdAt,
            userId,
            userName,
            userType,
            isPointActivity,
            pointName,
            pointType,
        } = activity;
        const status = changes && (changes.status || []);

        const isAdmin = userType && userType !== "driver";

        const activityList = [];

        if (activity.confirmedById) {
            activityList.push(
                generateActivityItem(
                    {
                        actorName: activity.confirmedByEmail,
                        actorType: "admin",
                        id: activity.confirmedById,
                    },
                    createdAt,
                    adminAvatarOptions,
                    `Confirmed ${_.startCase(activity.fieldName)}`
                )
            );
        }
        if (changes && changes.id && !isPointActivity) {
            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    <FormattedMessage id="trip.Created-trip" />
                )
            );
        }

        if (changes && changes.driver) {
            const actionDescription =
                changes.driver[0] && changes.driver[1] ? (
                    <FormattedMessage id="trip.trip-activity-changed-driver-from" />
                ) : changes.driver[0] && !changes.driver[1] ? (
                    <FormattedMessage id="trip.trip-activity-removed-driver" />
                ) : (
                    <FormattedMessage id="trip.trip-activity-assigned-driver" />
                );

            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    changes.driver[1],
                    driverAvatarOptions,
                    changes.driver[0],
                    <FormattedMessage id="driver" />
                )
            );
        }

        if (changes && changes.truck) {
            const actionDescription =
                changes.truck[0] && changes.truck[1] ? (
                    <FormattedMessage id="trip.trip-activity-changed-truck" />
                ) : changes.truck[0] && !changes.truck[1] ? (
                    <FormattedMessage id="trip.trip-activity-removed-truck" />
                ) : (
                    <FormattedMessage id="trip.trip-activity-assigned-truck" />
                );

            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    changes.truck[1]?.plateNumber,
                    false,
                    changes.truck[0]?.plateNumber
                )
            );
        }

        if (changes && changes.tripTypeId) {
            const actionDescription =
                changes.tripTypeId[0] && changes.tripTypeId[1] ? (
                    <FormattedMessage id="trip.trip-activity-changed-trip-type" />
                ) : changes.tripTypeId[0] && !changes.tripTypeId[1] ? (
                    <FormattedMessage id="trip.trip-activity-removed-trip-type" />
                ) : (
                    <FormattedMessage id="trip.trip-activity-assigned-trip-type" />
                );

            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    undefined,
                    false
                )
            );
        }

        if (changes && changes.tripType) {
            const actionDescription =
                changes.tripType[0] && changes.tripType[1] ? (
                    <FormattedMessage id="trip.trip-activity-changed-trip-type-from" />
                ) : changes.tripType[0] && !changes.tripType[1] ? (
                    <FormattedMessage id="trip.trip-activity-removed-trip-type" />
                ) : (
                    <FormattedMessage id="trip.trip-activity-assigned-trip-type" />
                );

            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    _.startCase(changes.tripType[1] && changes.tripType[1].name),
                    false,
                    changes.tripType[0] && _.startCase(changes.tripType[0].name)
                )
            );
        }

        if (changes && changes.shipmentNumberOfUnits) {
            const actionDescription =
                changes.shipmentNumberOfUnits[0] && changes.shipmentNumberOfUnits[1] ? (
                    <>
                        <FormattedMessage id="changed" /> {pointType}({pointName})
                        <FormattedMessage id="trip.trip-activity-shipment-num-unit-from" />
                    </>
                ) : changes.shipmentNumberOfUnits[0] && !changes.shipmentNumberOfUnits[1] ? (
                    <>
                        <FormattedMessage id="removed" />
                        {pointType} ({pointName})
                        <FormattedMessage id="trip.trip-activity-shipment-num-unit" />
                    </>
                ) : (
                    <>
                        <FormattedMessage id="assigned" />
                        {pointType} ({pointName}){" "}
                        <FormattedMessage id="trip.trip-activity-shipment-num-unit" />
                    </>
                );

            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    changes.shipmentNumberOfUnits[1],
                    false,
                    changes.shipmentNumberOfUnits[0] > 0 && changes.shipmentNumberOfUnits[0]
                )
            );
        }

        if (changes && changes.manualDistance) {
            const actionDescription =
                changes.manualDistance[0] && changes.manualDistance[1] ? (
                    <FormattedMessage id="trip.trip-activity-changed-trip-distance-from" />
                ) : changes.manualDistance[0] && !changes.manualDistance[1] ? (
                    <FormattedMessage id="trip.trip-activity-removed-trip-distance" />
                ) : (
                    <FormattedMessage id="trip.trip-activity-assigned-trip-distance" />
                );

            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    changes.manualDistance[1],
                    false,
                    changes.manualDistance[0]
                )
            );
        }

        if (changes && changes.contractType) {
            const actionDescription =
                changes.contractType[0] && changes.contractType[1] ? (
                    <FormattedMessage id="trip.trip-activity-Changed-trip-contract-from" />
                ) : changes.contractType[0] && !changes.contractType[1] ? (
                    <FormattedMessage id="trip.trip-activity-removed-trip-contract" />
                ) : (
                    <FormattedMessage id="trip.trip-activity-assigned-trip-contract" />
                );

            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    changes.contractType[1] && _.startCase(changes.contractType[1]),
                    false,
                    changes.contractType[0] && _.startCase(changes.contractType[0])
                )
            );
        }

        if (changes && changes.helpersCount) {
            const actionDescription =
                changes.helpersCount[0] && changes.helpersCount[1] ? (
                    <FormattedMessage id="trip.trip-activity-changed-helpers-count-from" />
                ) : changes.helpersCount[0] && !changes.helpersCount[1] ? (
                    <FormattedMessage id="trip.trip-activity-removed-helpers-count" />
                ) : (
                    <FormattedMessage id="trip.trip-activity-assigned-helpers-count" />
                );

            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    changes.helpersCount[1],
                    false,
                    changes.helpersCount[0]
                )
            );
        }

        if (changes && changes.scalesCount) {
            const actionDescription =
                changes.scalesCount[0] && changes.scalesCount[1] ? (
                    <FormattedMessage id="trip.trip-activity-changed-scales-count-from" />
                ) : changes.scalesCount[0] && !changes.scalesCount[1] ? (
                    <FormattedMessage id="trip.trip-activity-removed-scales-count" />
                ) : (
                    <FormattedMessage id="trip.trip-activity-assigned-scales-count" />
                );
            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    changes.scalesCount[1],
                    false,
                    changes.scalesCount[0]
                )
            );
        }

        if (changes && changes.tollStationGatesCount) {
            const actionDescription =
                changes.tollStationGatesCount[0] && changes.tollStationGatesCount[1] ? (
                    <FormattedMessage id="trip.trip-activity-changed-toll-station-gates-count-from" />
                ) : changes.tollStationGatesCount[0] && !changes.tollStationGatesCount[1] ? (
                    <FormattedMessage id="trip.trip-activity-removed-toll-station-gates-count" />
                ) : (
                    <FormattedMessage id="trip.trip-activity-assigned-toll-station-gates-count" />
                );

            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    changes.tollStationGatesCount[1],
                    false,
                    changes.tollStationGatesCount[0]
                )
            );
        }
        if (changes && changes.preTripDistance) {
            const actionDescription =
                changes.preTripDistance[0] && changes.preTripDistance[1] ? (
                    <FormattedMessage id="trip.trip-activity-changed-pre-trip-distance-from" />
                ) : changes.preTripDistance[0] && !changes.preTripDistance[1] ? (
                    <FormattedMessage id="trip.trip-activity-removed-pre-trip-distance" />
                ) : (
                    <FormattedMessage id="trip.trip-activity-assigned-pre-trip-distance" />
                );

            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    changes.preTripDistance[1],
                    false,
                    changes.preTripDistance[0]
                )
            );
        }

        if (changes && changes.shipmentUnit) {
            const actionDescription =
                changes.shipmentUnit[0] && changes.shipmentUnit[1] ? (
                    <FormattedMessage id="trip.trip-activity-changed-trip-shipment-unit-from" />
                ) : changes.shipmentUnit[0] && !changes.shipmentUnit[1] ? (
                    <FormattedMessage id="trip.trip-activity-removed-trip-shipment-unit" />
                ) : (
                    <FormattedMessage id="trip.trip-activity-assigned-trip-shipment-unit" />
                );

            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    changes.shipmentUnit[1] && _.startCase(changes.shipmentUnit[1]),
                    false,
                    changes.shipmentUnit[0] && _.startCase(changes.shipmentUnit[0])
                )
            );
        }

        if (changes && changes.source) {
            const actionDescription =
                changes.source[0] && changes.source[1] ? (
                    <FormattedMessage id="trip.trip-activity-changed-trip-source-form" />
                ) : changes.source[0] && !changes.source[1] ? (
                    <FormattedMessage id="trip.trip-activity-removed-trip-source" />
                ) : (
                    <FormattedMessage id="trip.trip-activity-assigned-trip-source" />
                );

            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    changes.source[1] && _.startCase(changes.source[1]),
                    false,
                    changes.source[0] && _.startCase(changes.source[0])
                )
            );
        }

        if (changes && changes.podNumber) {
            const actionDescription =
                changes.podNumber[0] && changes.podNumber[1] ? (
                    <>
                        <FormattedMessage id="changed" /> {pointType} ({pointName}){" "}
                        <FormattedMessage id="trip.trip-activity-POD-Number-from" />
                    </>
                ) : changes.podNumber[0] && !changes.podNumber[1] ? (
                    <>
                        <FormattedMessage id="removed" /> {pointType} ({pointName}){" "}
                        <FormattedMessage id="trip.trip-activity-POD-Number" />
                    </>
                ) : (
                    <>
                        <FormattedMessage id="assigned" /> {pointType} ({pointName}){" "}
                        <FormattedMessage id="trip.trip-activity-POD-Number" />
                    </>
                );

            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    changes.podNumber[1],
                    false,
                    changes.podNumber[0] > 0 && changes.podNumber[0]
                )
            );
        }

        if (changes && changes.tagList) {
            const actionDescription =
                changes.tagList[0] && changes.tagList[1] ? (
                    <FormattedMessage id="trip.changed-Tags-from" />
                ) : !changes.tagList[0] && changes.tagList[1].length === 0 ? (
                    <FormattedMessage id="trip.removed-Tags" />
                ) : (
                    <FormattedMessage id="trip.assigned-Tags" />
                );

            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    changes.tagList[1],
                    false,
                    changes.tagList[0]
                )
            );
        }
        if (changes && changes.arrivalTime) {
            const actionDescription =
                changes.arrivalTime[0] && changes.arrivalTime[1] ? (
                    <FormattedMessage id="trip.trip-activity-changed-arrival-time" />
                ) : !changes.arrivalTime[0] && changes.arrivalTime[1].length === 0 ? (
                    <FormattedMessage id="trip.trip-activity-removed-arrival-time" />
                ) : (
                    <FormattedMessage id="trip.trip-activity-assigned-arrival-time" />
                );

            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    moment(changes.arrivalTime[1]).format("DD-MM-YYYY  h:mm:ss a"),
                    false,
                    changes.arrivalTime[0]
                        ? moment(changes.arrivalTime[0]).format("DD-MM-YYYY  h:mm:ss a")
                        : null
                )
            );
        }
        if (changes && changes.departureTime) {
            const actionDescription =
                changes.departureTime[0] && changes.departureTime[1] ? (
                    <FormattedMessage id="trip.trip-activity-changed-departure-time" />
                ) : !changes.departureTime[0] && changes.departureTime[1].length === 0 ? (
                    <FormattedMessage id="trip.trip-activity-removed-departure-time" />
                ) : (
                    <FormattedMessage id="trip.trip-activity-assigned-departure-time" />
                );
            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    moment(changes.departureTime[1]).format("DD-MM-YYYY  h:mm:ss a"),
                    false,
                    changes.departureTime[0]
                        ? moment(changes.departureTime[0]).format("DD-MM-YYYY  h:mm:ss a")
                        : null
                )
            );
        }
        if (changes && changes.notes) {
            const actionDescription =
                changes.notes[0] && changes.notes[1] ? (
                    <FormattedMessage id="trip.trip-activity-changed-notes" />
                ) : changes.notes[0] && changes.notes[1] === "" ? (
                    <FormattedMessage id="trip.trip-activity-removed-notes" />
                ) : (
                    <FormattedMessage id="trip.trip-activity-assigned-notes" />
                );

            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    actionDescription,
                    null,
                    false,
                    null
                )
            );
        }
        if (status && status[0] === "pending" && status[1] === "moving") {
            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    <FormattedMessage id="trip.Started-trip" />
                )
            );
        }

        if (status && isStatusAction(status[1])) {
            activityList.push(
                generateActivityItem(
                    { actorName: userName, actorType: userType, id: userId },
                    createdAt,
                    adminAvatarOptions,
                    <>
                        <FormattedMessage id="trip.Changed-trip-state" />
                        <TripBadge status={status[1]} />
                    </>
                )
            );
        }

        if (status && (status[1] === "arrived" || status[1] === "moving")) {
            let lastArrivedPointId;
            const statusDesc = status[1] === "arrived" ? "Arrived at" : "Left";

            for (let i = activityIndex; i >= 0; i--) {
                const prevChanges = activities[i].changes;

                if (prevChanges && prevChanges.currentTripPointId) {
                    lastArrivedPointId =
                        prevChanges.currentTripPointId[status[1] === "arrived" ? 1 : 0];
                    break;
                }
            }

            if (lastArrivedPointId) {
                if (lastArrivedPointId === +pickupPoint.id) {
                    const locationAttributes =
                        mapResourceObjectToArray(pickupPoint.locationAttributes)[0] || {};

                    activityList.push(
                        generateActivityItem(
                            { actorName: userName, actorType: userType, id: userId },
                            createdAt,
                            adminAvatarOptions,
                            <>
                                {statusDesc} <FormattedMessage id="trip.pickup-location" /> (
                                {locationAttributes.name || locationAttributes.address})
                            </>
                        )
                    );
                } else {
                    const arrivedDropoffPoint = findPointById(lastArrivedPointId);
                    const locationAttributes =
                        mapResourceObjectToArray(arrivedDropoffPoint.locationAttributes)[0] || {};

                    activityList.push(
                        generateActivityItem(
                            { actorName: userName, actorType: userType, id: userId },
                            createdAt,
                            adminAvatarOptions,
                            <>
                                {statusDesc} <FormattedMessage id="trip.dropoff-location" /> (
                                {locationAttributes.name || locationAttributes.address})
                            </>
                        )
                    );
                }
            }
        }

        if (isPointActivity) {
            const isPointChanged = !!changes.id;
            const locationAttributes =
                mapResourceObjectToArray(changesPoint.locationAttributes)[0] || {};
            const address = locationAttributes.name || locationAttributes.address;

            if (isPointChanged && changesPoint.pointType === "pickup" && isAdmin) {
                activityList.push(
                    generateActivityItem(
                        { actorName: userName, actorType: userType, id: userId },
                        createdAt,
                        adminAvatarOptions,
                        <>
                            <FormattedMessage id="trip.Entered-pickup-details" />({address})
                        </>
                    )
                );
            }

            if (isPointChanged && changesPoint.pointType === "dropoff") {
                activityList.push(
                    generateActivityItem(
                        { actorName: userName, actorType: userType, id: userId },
                        createdAt,
                        adminAvatarOptions,
                        <>
                            <FormattedMessage id="trip.Entered-pickup-details" />({address})
                        </>
                    )
                );
            }
        }
        return activityList;
    };

    function findPointById(pointId) {
        return (pointId && tripPoints.find((point) => +point.id === pointId)) || {};
    }

    const pointActivities = tripPoints
        .map((point) =>
            point.activity.map((activity) => {
                const location = extractResourceAttributes(point.locationAttributes);
                return {
                    ...activity,
                    pointName: location.name || location.address,
                    pointType: point.pointType,
                };
            })
        )
        .flat()
        .map((activity) => ({ ...activity, isPointActivity: true }));

    const allActivities = [...activities, ...pointActivities, ...confirmedTripsWithAdmin].sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    return (
        <TripSection
            headerTitle="Activity"
            headerIconType="online"
            isActivitySection
        >
            <S.TripActivityWrapper>
                {activities.length ? (
                    allActivities
                        .map((activity, index, activities) =>
                            generateActivity(
                                activity,
                                pickupPoint,
                                activities,
                                index,
                                findPointById(
                                    activity.isPointActivity &&
                                        activity.changes.id &&
                                        +activity.changes.id[1]
                                )
                            )
                        )
                        .flat()
                ) : (
                    <FormattedMessage id="trip.No-activities-yet" />
                )}
            </S.TripActivityWrapper>
        </TripSection>
    );
}
